import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Autocomplete, TextField } from "@mui/material";
import newRequest from "../../../utils/userRequest";
import { handleUserError } from "../../../utils/error";
import axios from "axios";
import { gtrackUrl } from "../../../utils/config";

const AssetCapture = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const hasFetchedRef = useRef(false);
  const hasFetchedStatesRef = useRef(false);
  const hasFetchedCitiesRef = useRef(false);

  const [formData, setFormData] = useState({
    country: "SA",
    countryName: "Saudi Arabia",
    state: "",
    stateName: "",
    city: "",
    cityName: "",
    area: "",
    department: "",
    departmentCode: "",
    businessName: "",
    bldgName: "",
    bldgAddress: "",
    buildingNo: "",
    floorNo: "",
  });

  const fetchCountries = async () => {
    try {
      const res = await axios.get(gtrackUrl + "/countries");
      setCountries(res?.data || []);
    } catch (err) {
      handleUserError(err);
    }
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchCountries();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchStates = async () => {
    if (!formData.country) {
      setStates([]);
      return;
    }
    try {
      const res = await axios.get(gtrackUrl + 
        `/states?country_id=${formData.country}`
      );
      setStates(res?.data || []);
    } catch (err) {
      handleUserError(err);
    }
  };

  useEffect(() => {
    if (!hasFetchedStatesRef.current && formData.country) {
      fetchStates();
      hasFetchedStatesRef.current = true;
    }
  }, [formData.country]);

  const fetchCities = async () => {
    if (!formData.state) {
      setCities([]);
      return;
    }
    try {
      const res = await axios.get(gtrackUrl + 
        `/cities?state_id=${formData.state}`
      );
      setCities(res?.data || []);
    } catch (err) {
      handleUserError(err);
    }
  };

  useEffect(() => {
    if (!hasFetchedCitiesRef.current && formData.state) {
      fetchCities();
      hasFetchedCitiesRef.current = true;
    }
  }, [formData.state]);

  /* ------------------ HANDLERS ------------------ */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    const requiredFields = [
      { name: "country", label: "Country" },
      { name: "state", label: "State" },
      { name: "city", label: "City" },
      { name: "floorNo", label: "Floor Number" },
    ];

    const missing = requiredFields.filter(
      (field) => !formData[field.name]
    );

    if (missing.length) {
      handleUserError(
        `Please fill in all required fields: ${missing
          .map((f) => f.label)
          .join(", ")}`
      );
      return;
    }

    navigate("/member/category", { state: formData });
  };

  return (
    <div className={`p-0 h-full ${i18n.language === "ar" ? "sm:mr-10" : "sm:ml-10"}`}>
      <div className="p-4 border-2 border-gray-200 border-dashed rounded-lg">
        {/* FORM */}
        <div className="max-w-2xl mx-auto px-4 bg-gray-100 rounded-md mb-6 py-3 space-y-4">

          {/* COUNTRY */}
          <Autocomplete
            options={countries}
            getOptionLabel={(o) => o.name_en || ""}
            value={
              countries.find(
                (c) => c.country_shortName === formData.country
              ) || null
            }
            onChange={(_, v) => {
              hasFetchedStatesRef.current = false;
              hasFetchedCitiesRef.current = false;
              setFormData((p) => ({
                ...p,
                country: v?.country_shortName || "",
                countryName: v?.name_en || "",
                state: "",
                city: "",
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Country *" size="small" />
            )}
          />

          {/* STATE */}
          <Autocomplete
            options={states}
            getOptionLabel={(o) => o.name || ""}
            value={states.find((s) => s.id === formData.state) || null}
            onChange={(_, v) => {
              hasFetchedCitiesRef.current = false;
              setFormData((p) => ({
                ...p,
                state: v?.id || "",
                stateName: v?.name || "",
                city: "",
              }));
            }}
            disabled={!formData.country}
            renderInput={(params) => (
              <TextField {...params} label="State *" size="small" />
            )}
          />

          {/* CITY */}
          <Autocomplete
            options={cities}
            getOptionLabel={(o) => o.name || ""}
            value={cities.find((c) => c.id === formData.city) || null}
            onChange={(_, v) =>
              setFormData((p) => ({
                ...p,
                city: v?.id || "",
                cityName: v?.name || "",
              }))
            }
            disabled={!formData.state}
            renderInput={(params) => (
              <TextField {...params} label="City *" size="small" />
            )}
          />

          {/* AREA */}
          <input name="area" value={formData.area} onChange={handleChange}
            className="w-full p-2 border rounded" placeholder="Area" />

          {/* DEPARTMENT */}
          <select name="department" value={formData.department} onChange={handleChange}
            className="w-full p-2 border rounded">
            <option value="">Select</option>
            <option value="HR">Human Resources</option>
            <option value="IT">Information Technology</option>
            <option value="FIN">Finance</option>
            <option value="MKT">Marketing</option>
            <option value="OPS">Operations</option>
            <option value="SALES">Sales</option>
            <option value="RND">Research & Development</option>
            <option value="ADMIN">Administration</option>
          </select>

          {/* DEPARTMENT CODE */}
          <input name="departmentCode" value={formData.departmentCode}
            onChange={handleChange} className="w-full p-2 border rounded"
            placeholder="Department Code" />

          {/* BUSINESS NAME */}
          <input name="businessName" value={formData.businessName}
            onChange={handleChange} className="w-full p-2 border rounded"
            placeholder="Business Name" />

          {/* BUILDING NAME */}
          <input name="bldgName" value={formData.bldgName}
            onChange={handleChange} className="w-full p-2 border rounded"
            placeholder="BLDG. NAME" />

          {/* BUILDING ADDRESS */}
          <input name="bldgAddress" value={formData.bldgAddress}
            onChange={handleChange} className="w-full p-2 border rounded"
            placeholder="BLDG. ADDRESS" />

          {/* BUILDING NO */}
          <input name="buildingNo" value={formData.buildingNo}
            onChange={handleChange} className="w-full p-2 border rounded"
            placeholder="BUILDING NO." />

          {/* FLOOR */}
          <select name="floorNo" value={formData.floorNo}
            onChange={handleChange} className="w-full p-2 border rounded">
            <option value="">Select</option>
            <option value="1">1st Floor</option>
            <option value="2">2nd Floor</option>
            <option value="3">3rd Floor</option>
            <option value="4">4th Floor</option>
            <option value="5">5th Floor</option>
          </select>

          {/* ACTIONS */}
          <div className="flex justify-between">
            <Button
              variant="contained"
              sx={{ backgroundColor: "#E02266" }}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#E02266" }}
              onClick={handleNext}
            >
              Next
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AssetCapture;
